import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, FileText, Euro, Clock, CheckCircle2, ArrowRight, Landmark } from "lucide-react";
import api from "@/lib/api";
import { eur, deDate, badgeFor } from "@/lib/format";
import { VatQuarterly } from "@/components/VatQuarterly";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { readOnly } = useAuth();
  const navigate = useNavigate();
  const { data: invoices = [] } = useQuery({ queryKey: ["invoices"], queryFn: async () => (await api.get("/invoices")).data });
  const { data: customers = [] } = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data });

  const totalGross = invoices.reduce((s, i) => s + (i.gross || 0), 0);
  const open = invoices.filter((i) => i.status === "offen");
  const paid = invoices.filter((i) => i.status === "bezahlt");
  const openSum = open.reduce((s, i) => s + (i.gross || 0), 0);

  const stats = [
    { label: "Rechnungen gesamt", value: invoices.length, icon: FileText, tint: "text-slate-900" },
    { label: "Umsatz (brutto)", value: eur(totalGross), icon: Euro, tint: "text-slate-900", mono: true },
    { label: "Offen", value: eur(openSum), icon: Clock, tint: "text-amber-600", mono: true },
    { label: "Bezahlt", value: paid.length, icon: CheckCircle2, tint: "text-emerald-600" },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Übersicht</h1>
          <p className="text-sm text-slate-500 mt-1">Ihr Rechnungsüberblick auf einen Blick.</p>
        </div>
        <button
          onClick={() => navigate("/invoices/new")}
          disabled={readOnly}
          data-testid="dashboard-create-invoice"
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2.5 rounded-lg inline-flex items-center gap-2 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="h-4 w-4" /> Neue Rechnung
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="bg-white border border-slate-200/80 rounded-xl p-5" data-testid={`stat-${s.label}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-slate-500">{s.label}</span>
              <s.icon className={`h-4 w-4 ${s.tint}`} />
            </div>
            <div className={`mt-3 text-2xl font-bold ${s.tint} ${s.mono ? "font-mono tabular-nums" : "font-display"}`}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200/80 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="font-display text-lg font-semibold">Letzte Rechnungen</h2>
          <button onClick={() => navigate("/invoices")} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
            Alle ansehen <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
        {invoices.length === 0 ? (
          <div className="px-5 py-14 text-center text-sm text-slate-400">
            Noch keine Rechnungen. Erstellen Sie Ihre erste Rechnung.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {invoices.slice(0, 6).map((inv) => {
              const st = badgeFor(inv);
              return (
                <button
                  key={inv.id}
                  onClick={() => navigate(`/invoices/${inv.id}/edit`)}
                  className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 transition-colors text-left"
                  data-testid={`dashboard-invoice-${inv.number}`}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <span className="font-mono text-sm font-medium text-slate-900">{inv.number}</span>
                    <span className="text-sm text-slate-600 truncate">{inv.customer?.company_name || inv.customer?.contact || "—"}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-slate-400 hidden sm:inline">{deDate(inv.invoice_date)}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
                    <span className="font-mono tabular-nums text-sm font-semibold w-24 text-right">{eur(inv.gross)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200/80 rounded-xl overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100">
          <Landmark className="h-4 w-4 text-slate-500" />
          <h2 className="font-display text-lg font-semibold">Umsatzsteuer (Quartal)</h2>
        </div>
        <div className="p-5">
          <VatQuarterly initialYear={new Date().getFullYear()} showYearSelector={false} />
        </div>
      </div>

      <p className="text-xs text-slate-400">{customers.length} Kunden in Ihrer Datenbank.</p>
    </div>
  );
}
